<template>
  <div/>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import { useStore } from 'vuex';
import { latestMessage, type WebSocketMessageWithFields, type WebSocketMessageEmpty } from '@/plugins/ws';

import {RootState} from "@/store";

const store = useStore<RootState>();

defineOptions({
  name: 'dataExchanges',
});

// Базовый тип для сообщений с ID биржи
type ExchangeWithId = {
  idExchange: number;
};

// Базовый тип для create/update операций бирж
type ExchangeCreateUpdateData = ExchangeWithId & {
  code: string;
};

// Типы для WebSocket сообщений компонента exchanges
type ExchangeRootMessage = WebSocketMessageWithFields<'exchange-currency' | 'exchange-pair' | 'exchange-root', {
  exchange?: {
    idExchange: number;
    code: string;
    locked: boolean;
    blockStore: boolean;
    blockStream: boolean;
  };
}>;
type ExchangeRootNotFoundMessage = WebSocketMessageEmpty<'exchange-root-not-found'>;
type ExchangeListMessage = WebSocketMessageWithFields<'global-pair-exchange' | 'exchange-list' | 'pair' | 'store-info' | 'helper-agents', {
  exchanges?: unknown[];
}>;
type ExchangeCreateMessage = WebSocketMessageWithFields<'exchange-create' | 'exchange-create-success', ExchangeCreateUpdateData>;
type ExchangeUpdateMessage = WebSocketMessageWithFields<'exchange-update' | 'exchange-update-success', ExchangeCreateUpdateData>;
type ExchangeStatusMessage = WebSocketMessageWithFields<'exchange-status' | 'exchange-status-success', ExchangeWithId & {
  blockStore: boolean;
  blockStream: boolean;
}>;
type ExchangeDeleteMessage = WebSocketMessageWithFields<'exchange-delete' | 'exchange-delete-success', ExchangeWithId>;
type ExchangeLockMessage = WebSocketMessageWithFields<'exchange-lock' | 'exchange-lock-success', ExchangeWithId>;
type ExchangeUnlockMessage = WebSocketMessageWithFields<'exchange-unlock' | 'exchange-unlock-success', ExchangeWithId>;

// Объединенный тип для всех сообщений
type ExchangesWebSocketMessage = 
  | ExchangeRootMessage 
  | ExchangeRootNotFoundMessage 
  | ExchangeListMessage 
  | ExchangeCreateMessage 
  | ExchangeUpdateMessage 
  | ExchangeStatusMessage 
  | ExchangeDeleteMessage 
  | ExchangeLockMessage 
  | ExchangeUnlockMessage;

// WebSocket message handler
const handleWebSocketMessage = (data: ExchangesWebSocketMessage): void => {
  if (!data?.signal) return;

  switch (data.signal) {
    case 'exchange-currency':
    case 'exchange-pair':
    case 'exchange-root':
      if (data.exchange) {
        store.dispatch('exchange/wsSetExchange', {
          idExchange: data.exchange.idExchange,
          code: data.exchange.code,
          locked: data.exchange.locked,
          blockStore: data.exchange.blockStore,
          blockStream: data.exchange.blockStream,
        });
      }
      break;
    case 'exchange-root-not-found':
      store.dispatch('exchange/wsSetEmptyExchange');
      break;
    case 'global-pair-exchange':
    case 'exchange-list':
    case 'pair':
    case 'store-info':
    case 'helper-agents':
      if (data.exchanges) {
        store.dispatch('exchanges/wsSetExchanges', { exchanges: data.exchanges });
      }
      break;
    case 'exchange-create':
    case 'exchange-create-success':
      store.dispatch('exchanges/wsExchangeCreate', {
        idExchange: data.idExchange,
        code: data.code,
      });
      break;
    case 'exchange-update':
    case 'exchange-update-success':
      store.dispatch('exchanges/wsExchangeUpdate', {
        idExchange: data.idExchange,
        code: data.code,
      });
      store.dispatch('exchange/wsExchangeUpdate', {
        idExchange: data.idExchange,
        code: data.code,
      });
      break;
    case 'exchange-status':
    case 'exchange-status-success':
      store.dispatch('exchanges/wsExchangeStatus', {
        idExchange: data.idExchange,
        blockStore: data.blockStore,
        blockStream: data.blockStream,
      });
      store.dispatch('exchange/wsExchangeUpdate', {
        idExchange: data.idExchange,
        blockStore: data.blockStore,
        blockStream: data.blockStream,
      });
      break;
    case 'exchange-delete':
    case 'exchange-delete-success':
      store.dispatch('exchanges/wsExchangeDelete', {
        idExchange: data.idExchange,
      });
      store.dispatch('exchange/wsExchangeDelete', {
        idExchange: data.idExchange,
      });
      break;
    case 'exchange-lock':
    case 'exchange-lock-success':
      store.dispatch('exchanges/wsExchangeLock', {
        idExchange: data.idExchange,
      });
      store.dispatch('exchange/wsExchangeLock', {
        idExchange: data.idExchange,
      });
      break;
    case 'exchange-unlock':
    case 'exchange-unlock-success':
      store.dispatch('exchanges/wsExchangeUnlock', {
        idExchange: data.idExchange,
      });
      store.dispatch('exchange/wsExchangeUnlock', {
        idExchange: data.idExchange,
      });
      break;
  }
};

watch(latestMessage, handleWebSocketMessage);
</script>
